import { ref, onUnmounted, computed } from 'vue'

export interface AppStatusResponse {
  status: 'ACTIVE' | 'UPCOMING_MAINTENANCE' | 'MAINTENANCE'
  maintenanceStartDate?: string
  maintenanceEndDate?: string
  message?: string
}

export function useMaintenanceStatus() {
  const baseUrl = import.meta.env.VITE_API_URL
  const appStatus = ref<AppStatusResponse | null>(null)
  const countdown = ref('')
  const isLoading = ref(false)

  let pollingInterval: ReturnType<typeof setInterval> | null = null
  let countdownInterval: ReturnType<typeof setInterval> | null = null

  const isUpcoming = computed(() => appStatus.value?.status === 'UPCOMING_MAINTENANCE')
  const isMaintenance = computed(() => appStatus.value?.status === 'MAINTENANCE')
  const isActive = computed(() => !appStatus.value || appStatus.value.status === 'ACTIVE')
  const message = computed(() => appStatus.value?.message || '')
  const maintenanceEndDate = computed(() => appStatus.value?.maintenanceEndDate || '')

  function formatCountdown(diffMs: number): string {
    if (diffMs <= 0) return '00:00:00'
    const totalSeconds = Math.floor(diffMs / 1000)
    const hours = Math.floor(totalSeconds / 3600)
    const minutes = Math.floor((totalSeconds % 3600) / 60)
    const seconds = totalSeconds % 60
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
  }

  function startCountdown() {
    stopCountdown()
    if (!appStatus.value?.maintenanceStartDate) return

    const targetDate = new Date(appStatus.value.maintenanceStartDate).getTime()

    const tick = () => {
      const now = Date.now()
      const diff = targetDate - now
      if (diff <= 0) {
        countdown.value = '00:00:00'
        stopCountdown()
        // Re-fetch immediately to get the MAINTENANCE status
        fetchStatus()
        return
      }
      countdown.value = formatCountdown(diff)
    }

    tick()
    countdownInterval = setInterval(tick, 1000)
  }

  function stopCountdown() {
    if (countdownInterval) {
      clearInterval(countdownInterval)
      countdownInterval = null
    }
    countdown.value = ''
  }

  async function fetchStatus() {
    try {
      isLoading.value = true
      const response = await fetch(`${baseUrl}/app-status`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      })

      if (!response.ok) {
        console.warn('[MaintenanceStatus] Error al consultar estado:', response.status)
        return
      }

      const data: AppStatusResponse = await response.json()
      appStatus.value = data

      if (data.status === 'UPCOMING_MAINTENANCE') {
        startCountdown()
      } else {
        stopCountdown()
      }
    } catch (err) {
      console.warn('[MaintenanceStatus] No se pudo consultar el estado del servidor:', err)
    } finally {
      isLoading.value = false
    }
  }

  function startPolling() {
    // Initial fetch
    fetchStatus()
    // Poll every 60 seconds
    pollingInterval = setInterval(fetchStatus, 60 * 1000)
  }

  function stopPolling() {
    if (pollingInterval) {
      clearInterval(pollingInterval)
      pollingInterval = null
    }
    stopCountdown()
  }

  // Auto start and cleanup
  startPolling()
  onUnmounted(() => {
    stopPolling()
  })

  return {
    appStatus,
    countdown,
    isUpcoming,
    isMaintenance,
    isActive,
    message,
    maintenanceEndDate,
    isLoading,
    fetchStatus,
    stopPolling,
  }
}

